import { createTheme, lighten, type Theme } from '@mui/material/styles';

// Expande a interface do TypeScript para suportar devHighlight
declare module '@mui/material/styles' {
  interface Palette {
    devHighlight?: Palette['primary'];
  }
  interface PaletteOptions {
    devHighlight?: PaletteOptions['primary'];
  }
}

// Função para detectar o ambiente
const getEnvironment = () => {
  const hostname = window.location.hostname;
  if (hostname === 'homol.horsetech.com.br') return 'homologacao';
  if (hostname === 'adm.horsetech.com.br') return 'production';
  if (hostname === 'localhost' || hostname.includes('127.0.0.1') || hostname.includes('192.168')) {
    return 'development';
  }
  const windowEnv = (window as any).env;
  if (windowEnv?.REACT_APP_ENV) return windowEnv.REACT_APP_ENV;
  const nodeEnv = windowEnv?.NODE_ENV || process.env.NODE_ENV;
  if (nodeEnv === 'development') return 'development';
  return 'production';
};

const environment = getEnvironment();

// Configurações por ambiente — a COR PRIMÁRIA varia por ambiente (prod azul /
// homol verde / dev laranja) para identificar onde se está.
const environmentConfig = {
  development: { primaryColor: '#ff9800', borderColor: '#ff9800', name: 'Desenvolvimento' },
  homologacao: { primaryColor: '#2e7d32', borderColor: '#2e7d32', name: 'Homologação' },
  production: { primaryColor: '#004AEE', borderColor: null, name: 'Produção' },
};

const config =
  environmentConfig[environment as keyof typeof environmentConfig] || environmentConfig.production;

export const ENV_PRIMARY = config.primaryColor;
export const ENV_NAME = config.name;

console.log(`Ambiente: ${environment} (${window.location.hostname}) | Primária: ${config.name} (${config.primaryColor})`);
// Sem badge de ambiente: o ambiente é identificado pela cor primária (laranja
// dev / verde homol / azul prod).

// ── Design tokens ────────────────────────────────────────────────────────────
const FONT = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';

const softShadow = (isDark: boolean) =>
  isDark
    ? '0 1px 2px rgba(0,0,0,0.45), 0 6px 20px rgba(0,0,0,0.45)'
    : '0 1px 2px rgba(16,24,32,0.04), 0 6px 20px rgba(16,24,32,0.07)';

export type AppColorMode = 'light' | 'dark';

/** Constrói o tema (claro ou escuro) a partir da cor primária do ambiente. */
export function getTheme(mode: AppColorMode): Theme {
  const isDark = mode === 'dark';
  // No escuro, clareia a primária para manter contraste/AA sobre fundo escuro.
  const primaryMain = isDark ? lighten(config.primaryColor, 0.18) : config.primaryColor;
  const border = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(16,24,32,0.08)';

  return createTheme({
    palette: {
      mode,
      primary: { main: primaryMain },
      // Acento da marca (ciano/teal) — nunca roxo (era o default do MUI).
      secondary: { main: isDark ? '#2DD4DE' : '#0E9BB0' },
      success: { main: isDark ? '#34D399' : '#1F9D57' },
      warning: { main: isDark ? '#FBBF24' : '#C9820B' },
      error: { main: isDark ? '#F87171' : '#DC3D43' },
      info: { main: isDark ? '#60A5FA' : '#2F74E0' },
      background: isDark
        ? { default: '#0E1116', paper: '#171C22' }
        : { default: '#F6F7F9', paper: '#FFFFFF' },
      text: isDark
        ? { primary: '#E6EAF0', secondary: '#9AA7B4' }
        : { primary: '#19222C', secondary: '#5B6B7B' },
      divider: border,
      ...(environment !== 'production' && { devHighlight: { main: config.primaryColor } }),
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: FONT,
      h1: { fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, letterSpacing: '-0.02em' },
      h3: { fontWeight: 700, letterSpacing: '-0.015em' },
      h4: { fontWeight: 600, letterSpacing: '-0.01em' },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 8, paddingInline: 16 } },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: 12 },
          outlined: { borderColor: border },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { borderRadius: 14, border: `1px solid ${border}`, boxShadow: softShadow(isDark) },
        },
      },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 16 } } },
      MuiMenu: { styleOverrides: { paper: { borderRadius: 12 } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 10 },
          // SENHA SALVA: o Chrome pinta o fundo do preenchimento automático
          // só no <input>, não no adorno ao lado. Num campo com ícone (o olho
          // do login, por exemplo) o retângulo azul parava antes dele, com
          // cantos próprios, parecendo campo quebrado.
          //
          // Não há como desligar esse fundo: a saída conhecida é cobri-lo com
          // uma sombra interna da cor do campo, que o box-shadow do autofill
          // não sobrepõe. A transição longa cobre o instante da animação.
          input: {
            '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
                {
              WebkitBoxShadow: `0 0 0 100px ${
                  isDark ? '#171C22' : '#FFFFFF'} inset`,
              WebkitTextFillColor: isDark ? '#E6EAF0' : '#19222C',
              caretColor: isDark ? '#E6EAF0' : '#19222C',
              borderRadius: 'inherit',
              transition: 'background-color 100000s ease-in-out 0s',
            },
          },
        },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 8, fontWeight: 500 } } },
      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 600, color: isDark ? '#9AA7B4' : '#5B6B7B' },
        },
      },
      MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 8, fontSize: 12 } } },
    },
  });
}

// Compatibilidade: alguns imports antigos usam o default export.
export default getTheme('light');
